export interface QueryParam {
  id: string;
  label: string;
  type: 'dropdown' | 'text';
  options?: { label: string; value: string }[];
  defaultValue: string;
  placeholder?: string;
  optional?: boolean;
}

export interface LineExplanation {
  label: string;
  text: string;
}

export interface KQLQuery {
  id: number;
  title: string;
  description: string;
  template: string;
  explanations: LineExplanation[];
  additionalParams?: QueryParam[];
  note?: string;
  tags?: string[];
}

export const GLOBAL_PARAMS = {
  timeRange: {
    options: [
      { label: '7 days', value: '7d' },
      { label: '14 days', value: '14d' },
      { label: '30 days', value: '30d' },
      { label: '60 days', value: '60d' },
      { label: '90 days', value: '90d' },
      { label: '180 days', value: '180d' },
      { label: '365 days', value: '365d' },
    ],
    default: '30d',
  },
  costPerGB: {
    options: [
      { label: '£2.167 — PAYG Analytics Logs', value: '2.167' },
      { label: '£0.46 — Basic Logs', value: '0.46' },
      { label: '£0.04 — Auxiliary Logs', value: '0.04' },
      { label: '£5.16 — Sentinel PAYG (combined)', value: '5.16' },
      { label: 'Custom...', value: 'custom' },
    ],
    default: '2.167',
  },
  currencySymbol: {
    options: [
      { label: '£ GBP', value: '£' },
      { label: '$ USD', value: '$' },
      { label: '€ EUR', value: '€' },
    ],
    default: '£',
  },
};

export const KQL_QUERIES: KQLQuery[] = [
  {
    id: 1,
    title: 'Ingestion by Resource Group',
    description: 'Aggregate billable ingestion and estimated cost by Azure resource group. Useful for team chargeback and showback.',
    template: `let CostPerGB = {{CostPerGB}};
let TimeRange = {{TimeRange}};
let CurrencySymbol = "{{CurrencySymbol}}";
find where TimeGenerated between(startofday(ago(TimeRange)) .. startofday(now()))
    project _ResourceId, _BilledSize, _IsBillable
| where _IsBillable == true
| summarize BillableDataBytes = sum(_BilledSize) by _ResourceId
| extend ResourceGroup = tostring(split(_ResourceId, "/")[4])
| summarize BillableDataBytes = sum(BillableDataBytes) by ResourceGroup
| extend DataIngestedInGB = round(BillableDataBytes / 1e9, 2)
| extend Cost = strcat(CurrencySymbol, round(DataIngestedInGB * CostPerGB, 2))
| project-away BillableDataBytes
| sort by DataIngestedInGB desc`,
    explanations: [
      { label: 'Lines 1–3: Variables', text: 'Edit these in the panel on the right. CostPerGB is your PAYG ingestion rate. These let-bindings make the query easy to customise.' },
      { label: 'Line 4: find operator', text: 'Scans ALL tables in the workspace for matching rows. Note: this can be resource-intensive on large workspaces with many tables.' },
      { label: 'Lines 5–6: Billable filter', text: '_IsBillable filters to only data you are charged for. Free tables like Heartbeat and AzureActivity are excluded from the cost total.' },
      { label: 'Lines 7–8: Resource group extraction', text: 'Extracts the resource group name from the ARM resource ID string (segment at position [4] in the "/" split).' },
      { label: 'Lines 9–10: Bytes to GB', text: 'Converts bytes to GB using Azure\'s billing standard (10⁹ bytes = 1 GB, not 1 GiB = 1,073,741,824 bytes).' },
      { label: 'Lines 11–12: Cost & tidy', text: 'Formats the cost as a currency string. project-away removes the raw bytes column — only GB and cost are shown in results.' },
    ],
    additionalParams: [
      {
        id: 'RGFilter',
        label: 'Filter by resource group (optional)',
        type: 'text',
        defaultValue: '',
        placeholder: 'e.g. production-rg',
        optional: true,
      },
    ],
    tags: ['Chargeback', 'FinOps'],
  },
  {
    id: 2,
    title: 'Ingestion by Table',
    description: 'Show billable data ingestion per Log Analytics table, sorted by volume. The first step in any cost investigation.',
    template: `let TimeRange = {{TimeRange}};
let CostPerGB = {{CostPerGB}};
let CurrencySymbol = "{{CurrencySymbol}}";
Usage
| where TimeGenerated > ago(32d)
| where StartTime >= startofday(ago(TimeRange))
    and EndTime < startofday(now())
| where IsBillable == true
| summarize BillableDataGB = sum(Quantity) / 1000 by Solution, DataType
| extend Cost = strcat(CurrencySymbol,
    round(BillableDataGB * CostPerGB, 2))
| sort by BillableDataGB desc`,
    explanations: [
      { label: 'Line 4: Usage table', text: 'Uses the Usage table — Azure\'s pre-aggregated billing table. Much faster than scanning all raw tables and the source of truth for invoicing.' },
      { label: 'Line 5: 32-day filter', text: '32-day TimeGenerated filter ensures the portal looks beyond its default 24-hour display range when querying historical data.' },
      { label: 'Lines 6–7: Billing window', text: 'StartTime/EndTime define the actual billing bucket — this is what appears on your Azure invoice, not TimeGenerated.' },
      { label: 'Line 8: Billable only', text: 'IsBillable == true filters out free data types. Many Azure-native tables ingest for free (Heartbeat, AzureActivity, etc.).' },
      { label: 'Line 9: MB to GB', text: 'Quantity in the Usage table is in MB — dividing by 1,000 converts to GB to match Azure\'s billing unit.' },
    ],
    tags: ['FinOps', 'Troubleshooting'],
  },
  {
    id: 3,
    title: 'Retention Cost Analysis',
    description: 'Estimate monthly retention costs per table beyond the free retention period.',
    template: `let RetentionCostPerGBMonth = {{RetentionCost}};
let InteractiveRetentionDays = {{RetentionDays}};
let FreeRetentionDays = 31;
let BillableRetentionDays = InteractiveRetentionDays - FreeRetentionDays;
let IngestionCostPerGB = {{CostPerGB}};
let TimeRange = {{TimeRange}};
let CurrencySymbol = "{{CurrencySymbol}}";
Usage
| where TimeGenerated > ago(32d)
| where StartTime >= startofday(ago(TimeRange))
    and EndTime < startofday(now())
| where IsBillable == true
| summarize TotalGB = sum(Quantity) / 1000 by DataType
| extend MonthlyRetentionCost = round(
    TotalGB * RetentionCostPerGBMonth
    * (BillableRetentionDays / 30.0), 2)
| extend TotalMonthlyCost = round(
    TotalGB * IngestionCostPerGB
    + MonthlyRetentionCost, 2)
| extend RetentionCost = strcat(CurrencySymbol, MonthlyRetentionCost)
| extend TotalCost = strcat(CurrencySymbol, TotalMonthlyCost)
| sort by TotalMonthlyCost desc`,
    explanations: [
      { label: 'Lines 1–7: Variables', text: 'BillableRetentionDays = configured retention minus the 31 free days included in every workspace. First 31 days are always free.' },
      { label: 'Lines 13–14: Per-table aggregation', text: 'Aggregates ingestion GB per table type for the selected time range.' },
      { label: 'Lines 15–17: Retention cost formula', text: 'Retention cost = GB × rate × (billable days / 30). Scales correctly for partial months.' },
      { label: 'Lines 18–19: Total cost', text: 'Combines ingestion cost + retention cost for a total monthly view per table.' },
    ],
    additionalParams: [
      {
        id: 'RetentionDays',
        label: 'Interactive retention (days)',
        type: 'dropdown',
        options: [
          { label: '31 days (minimum)', value: '31' },
          { label: '60 days', value: '60' },
          { label: '90 days', value: '90' },
          { label: '180 days', value: '180' },
          { label: '365 days (1 year)', value: '365' },
          { label: '730 days (2 years / FCA)', value: '730' },
        ],
        defaultValue: '90',
      },
      {
        id: 'RetentionCost',
        label: 'Retention rate (£/GB/month)',
        type: 'dropdown',
        options: [
          { label: '£0.0961 (standard)', value: '0.0961' },
          { label: 'Custom...', value: 'custom' },
        ],
        defaultValue: '0.0961',
      },
    ],
    tags: ['FCA Financial Services', 'NHS DSPT', 'FinOps'],
  },
  {
    id: 4,
    title: 'Free vs Billable Data',
    description: 'Compare billable vs free data ingestion per table — identify where you are being charged vs what Azure provides at no cost.',
    template: `let TimeRange = {{TimeRange}};
Usage
| where TimeGenerated > ago(32d)
| where StartTime >= startofday(ago(TimeRange))
    and EndTime < startofday(now())
| summarize
    BillableGB = round(
        sumif(Quantity, IsBillable == true)
        / 1000, 2),
    FreeGB = round(
        sumif(Quantity, IsBillable == false)
        / 1000, 2)
    by DataType
| extend TotalGB = round(BillableGB + FreeGB, 2)
| extend BillablePercent =
    round(100.0 * BillableGB / TotalGB, 1)
| sort by BillableGB desc`,
    explanations: [
      { label: 'Lines 6–12: Conditional aggregation', text: 'sumif() splits the total into two buckets in a single pass over the Usage table — more efficient than two separate queries.' },
      { label: 'Lines 13–15: Percentages', text: 'BillablePercent shows what fraction of each table\'s data you actually pay for. Tables with 0% are fully free (e.g. AzureActivity).' },
    ],
    tags: ['FinOps'],
  },
  {
    id: 5,
    title: 'Top Tables by Cost',
    description: 'Scan all workspace tables and rank by estimated cost. Use this to find unexpected data sources.',
    template: `let PricePerGB = {{CostPerGB}};
let CurrencySymbol = "{{CurrencySymbol}}";
union withsource=TableName *
| where TimeGenerated > ago({{TimeRange}})
| where _IsBillable == true
| summarize
    Entries = count(),
    TotalBytes = sum(_BilledSize),
    LastRecord = max(TimeGenerated)
    by TableName
| extend SizeGB = round(TotalBytes / 1e9, 2)
| extend EstimatedCost = strcat(
    CurrencySymbol,
    round(SizeGB * PricePerGB, 2))
| extend AvgBytesPerEntry =
    round(1.0 * TotalBytes / Entries, 0)
| project TableName, Entries, SizeGB,
    AvgBytesPerEntry, EstimatedCost, LastRecord
| sort by SizeGB desc`,
    explanations: [
      { label: 'Line 3: union *', text: 'union * scans all tables in the workspace simultaneously. This is a broad scan — consider scoping with a shorter time range on large workspaces.' },
      { label: 'Lines 6–10: Multi-metric aggregation', text: 'Collects entry count, total bytes, and last record timestamp per table in one pass.' },
      { label: 'Lines 15–16: AvgBytesPerEntry', text: 'Average bytes per log entry helps identify verbose tables. High avg bytes per entry = schema or verbosity optimisation opportunity.' },
    ],
    tags: ['Troubleshooting', 'FinOps'],
  },
  {
    id: 6,
    title: 'Daily Ingestion Trend',
    description: 'Visualise daily billable ingestion over time as a column chart. Useful for spotting spikes and growth trends.',
    template: `let TimeRange = {{TimeRange}};
Usage
| where TimeGenerated > ago(32d)
| where StartTime >= startofday(ago(TimeRange))
    and EndTime < startofday(now())
| where IsBillable == true
| summarize BillableDataGB = sum(Quantity) / 1000
    by bin(StartTime, 1d), DataType
| render columnchart`,
    explanations: [
      { label: 'Lines 7–8: bin() by day', text: 'bin(StartTime, 1d) buckets data into daily intervals aligned to midnight. Each bar in the chart represents one calendar day.' },
      { label: 'Line 9: render', text: 'render columnchart displays the results as a stacked column chart in the Azure Portal Logs view. Remove this line when exporting data.' },
    ],
    additionalParams: [
      {
        id: 'TableFilter',
        label: 'Filter to table (optional)',
        type: 'text',
        defaultValue: '',
        placeholder: 'e.g. SecurityEvent',
        optional: true,
      },
    ],
    tags: ['Troubleshooting', 'Reporting'],
  },
  {
    id: 7,
    title: 'Sentinel Security Data Costs',
    description: 'Estimate costs for the key security data types that Microsoft Sentinel ingests and analyses.',
    template: `let PricePerGB = {{SentinelCost}};
let CurrencySymbol = "{{CurrencySymbol}}";
let TimeRange = {{TimeRange}};
Usage
| where TimeGenerated > ago(32d)
| where StartTime >= startofday(ago(TimeRange))
    and EndTime < startofday(now())
| where IsBillable == true
| where DataType in (
    'SecurityEvent',
    'CommonSecurityLog',
    'Syslog',
    'SecurityAlert',
    'SigninLogs',
    'AADNonInteractiveUserSignInLogs',
    'AuditLogs',
    'OfficeActivity',
    'ThreatIntelligenceIndicator')
| summarize TotalGB = round(
    sum(Quantity) / 1000, 2) by DataType
| extend Cost = strcat(CurrencySymbol,
    round(TotalGB * PricePerGB, 2))
| sort by TotalGB desc`,
    explanations: [
      { label: 'Lines 9–18: Security table list', text: 'These are the primary Sentinel data sources. Add or remove table names to match your specific connectors and data sources.' },
      { label: 'Line 1: PricePerGB', text: 'Use £5.16 for the combined LA+Sentinel PAYG rate, or £3.975 if you want Sentinel analytics charges only (excluding ingestion).' },
    ],
    additionalParams: [
      {
        id: 'SentinelCost',
        label: 'Sentinel rate (£/GB)',
        type: 'dropdown',
        options: [
          { label: '£5.16 — Sentinel PAYG combined', value: '5.16' },
          { label: '£3.975 — Sentinel analytics only', value: '3.975' },
          { label: 'Custom...', value: 'custom' },
        ],
        defaultValue: '5.16',
      },
    ],
    tags: ['NHS DSPT', 'FCA Financial Services', 'Security'],
  },
  {
    id: 8,
    title: 'Data by Solution',
    description: 'Break down ingestion costs by Azure Monitor solution (e.g. Security, Updates, ServiceMap). Useful for solution-level chargeback.',
    template: `let TimeRange = {{TimeRange}};
let CostPerGB = {{CostPerGB}};
let CurrencySymbol = "{{CurrencySymbol}}";
Usage
| where TimeGenerated > ago(32d)
| where StartTime >= startofday(ago(TimeRange))
    and EndTime < startofday(now())
| where IsBillable == true
| summarize BillableDataGB =
    round(sum(Quantity) / 1000, 2) by Solution
| extend Cost = strcat(CurrencySymbol,
    round(BillableDataGB * CostPerGB, 2))
| sort by BillableDataGB desc`,
    explanations: [
      { label: 'Group by Solution', text: 'Solution is the Azure Monitor management solution that generated the data — e.g. Security, WindowsFirewall, Updates, ServiceMap. Useful for capability-level cost attribution.' },
    ],
    tags: ['FinOps', 'Chargeback'],
  },
  {
    id: 9,
    title: 'Workspace Comparison',
    description: 'Compare data volumes and costs across multiple Log Analytics workspaces in the same query.',
    template: `let TimeRange = {{TimeRange}};
let CostPerGB = {{CostPerGB}};
union withsource = SourceWorkspace *
| where TimeGenerated >= startofday(ago(TimeRange))
| where _IsBillable == true
| summarize TotalBytes = sum(_BilledSize)
    by SourceWorkspace, bin(TimeGenerated, 1d)
| summarize
    DailyAvgGB = round(avg(TotalBytes) / 1e9, 2),
    TotalGB = round(sum(TotalBytes) / 1e9, 2)
    by SourceWorkspace
| extend EstimatedMonthlyCost =
    round(TotalGB * CostPerGB, 2)
| sort by TotalGB desc`,
    explanations: [
      { label: 'Line 3: Cross-workspace union', text: 'union withsource= captures the originating workspace name in each row. Requires cross-workspace query permissions in your Azure AD tenant.' },
      { label: 'Lines 8–10: Daily average', text: 'DailyAvgGB averages daily bytes before summing — gives a more stable estimate than a raw total when the time range spans partial days.' },
    ],
    note: 'This query requires cross-workspace query permissions. Configure these in the Azure Portal under Log Analytics → Access Control (IAM) for each workspace.',
    tags: ['Enterprise', 'Chargeback'],
  },
  {
    id: 10,
    title: 'Anomaly Detection',
    description: 'Use built-in ML functions to detect unusual spikes or drops in data ingestion volume by table.',
    template: `let LookbackDays = {{LookbackDays}};
let BinSize = 1d;
Usage
| where TimeGenerated between (
    startofday(ago(LookbackDays)) ..
    startofday(now()))
| where IsBillable == true
| make-series ActualUsage = sum(Quantity)
    default = 0
    on TimeGenerated
    from startofday(ago(LookbackDays))
    to startofday(now())
    step BinSize
    by DataType
| extend (Anomalies, AnomalyScore,
    ExpectedUsage) =
    series_decompose_anomalies(ActualUsage)
| mv-expand
    ActualUsage to typeof(double),
    TimeGenerated to typeof(datetime),
    Anomalies to typeof(double),
    AnomalyScore to typeof(double),
    ExpectedUsage to typeof(long)
| where Anomalies != 0
| project TimeGenerated, DataType,
    ActualUsage, ExpectedUsage,
    AnomalyScore, Anomalies
| sort by abs(AnomalyScore) desc`,
    explanations: [
      { label: 'Lines 8–14: make-series', text: 'make-series creates a time-series array per DataType — required input format for the ML anomaly detection function.' },
      { label: 'Lines 15–17: series_decompose_anomalies', text: 'Built-in KQL ML function. Decomposes the time series into trend + seasonal + residual, then flags statistical outliers as anomalies.' },
      { label: 'Lines 18–23: mv-expand', text: 'mv-expand flattens the array columns back into individual rows — one row per day per DataType, enabling standard filtering and sorting.' },
      { label: 'Line 24: where Anomalies != 0', text: 'Anomalies = 1 means spike, -1 means drop, 0 means normal. This filters to only the unusual days.' },
    ],
    additionalParams: [
      {
        id: 'LookbackDays',
        label: 'Lookback period',
        type: 'dropdown',
        options: [
          { label: '14 days (minimum)', value: '14d' },
          { label: '21 days (recommended)', value: '21d' },
          { label: '30 days', value: '30d' },
          { label: '60 days', value: '60d' },
        ],
        defaultValue: '21d',
      },
    ],
    note: 'Minimum 14 days of data required for reliable anomaly baselines. Shorter windows will produce false positives.',
    tags: ['UK GDPR Audit', 'Security', 'FinOps'],
  },
  {
    id: 11,
    title: 'Calculator vs Actual',
    description: 'Compare your AzureCalc.uk estimate with actual ingestion and cost in your workspace. Validates calculation accuracy.',
    template: `let CostPerGB = {{CostPerGB}};
let TimeRange = {{TimeRange}};
let CalculatorDailyGB = {{CalculatorDailyGB}};
let CalculatorDailyCost = CalculatorDailyGB * CostPerGB;
Usage
| where TimeGenerated between (startofday(ago(TimeRange)) .. startofday(now()))
| where IsBillable == true
| summarize ActualDataGB = sum(Quantity) / 1000000000 by bin(TimeGenerated, 1d)
| extend ActualDailyAvg = todouble(round(ActualDataGB / 1, 2))
| extend CalculatorEstimate = CalculatorDailyGB
| extend Variance = round(ActualDailyAvg - CalculatorEstimate, 2)
| extend VariancePercent = round((Variance / CalculatorEstimate) * 100, 1)
| extend CostActual = round(ActualDailyAvg * CostPerGB, 2)
| extend CostEstimated = round(CalculatorDailyCost, 2)
| extend CostVariance = round(CostActual - CostEstimated, 2)
| project TimeGenerated, CalculatorEstimate, ActualDailyAvg, Variance, VariancePercent, CostEstimated, CostActual, CostVariance
| sort by TimeGenerated asc`,
    explanations: [
      { label: 'Lines 1–3: Parameters', text: 'CostPerGB and TimeRange are global. CalculatorDailyGB is your AzureCalc.uk estimate (in GB/day). Adjust in the panel on the right.' },
      { label: 'Lines 4–8: Actual usage', text: 'Sums billable Usage over the time range. Divides by 10⁹ to convert bytes to GB (matching Azure Retail Prices API standard).' },
      { label: 'Lines 9–10: Daily average', text: 'Averages actual ingestion per day. This is directly comparable to your calculator input.' },
      { label: 'Lines 11–15: Variance', text: 'Calculates the difference between estimate and actual, both in GB/day and as a percentage. Shows whether the calculator is high or low.' },
      { label: 'Lines 16–17: Cost comparison', text: 'Multiplies GB by rate to show estimated vs actual cost impact. A positive variance means you are ingesting more than estimated.' },
    ],
    additionalParams: [
      {
        id: 'CalculatorDailyGB',
        label: 'Your AzureCalc estimate (GB/day)',
        type: 'text',
        defaultValue: '10',
        placeholder: 'e.g. 25',
      },
    ],
    tags: ['Cost Verification', 'FinOps'],
  },
  {
    id: 12,
    title: 'Tier Optimizer',
    description: 'Recommends the best commitment tier based on actual daily ingestion. Shows break-even vs PAYG at current usage.',
    template: `let TimeRange = {{TimeRange}};
let PAYG_Rate = {{CostPerGB}};
Usage
| where TimeGenerated between (startofday(ago(TimeRange)) .. startofday(now()))
| where IsBillable == true
| summarize DailyGB = sum(Quantity) / 1e9 by bin(TimeGenerated, 1d)
| extend DailyAvg = round(avg(DailyGB), 2)
| extend Daily100GB_Cost = 180.20
| extend Daily200GB_Cost = 339.87
| extend Daily300GB_Cost = 498.73
| extend Daily500GB_Cost = 798.88
| extend PAYG_Monthly = round(DailyAvg * 30 * PAYG_Rate, 2)
| extend Tier100GB_Monthly = Daily100GB_Cost * 30
| extend Tier200GB_Monthly = Daily200GB_Cost * 30
| extend Tier300GB_Monthly = Daily300GB_Cost * 30
| extend Tier500GB_Monthly = Daily500GB_Monthly * 30
| extend BestTier = case(
    Tier100GB_Monthly < PAYG_Monthly and DailyAvg <= 100, "100 GB (best)",
    Tier200GB_Monthly < PAYG_Monthly and DailyAvg <= 200, "200 GB (best)",
    Tier300GB_Monthly < PAYG_Monthly and DailyAvg <= 300, "300 GB (best)",
    Tier500GB_Monthly < PAYG_Monthly and DailyAvg <= 500, "500 GB (best)",
    "PAYG (best)"
  )
| project DailyAvg, BestTier, PAYG_Monthly, Tier100GB_Monthly, Tier200GB_Monthly, Tier300GB_Monthly, Tier500GB_Monthly`,
    explanations: [
      { label: 'Lines 1–6: Daily aggregate', text: 'Calculates actual billable GB per day from the Usage table over the selected time range.' },
      { label: 'Lines 7–11: Tier pricing', text: 'Daily costs for each commitment tier. Values in GBP/day. Multiply by 30 to get monthly estimates.' },
      { label: 'Line 12: PAYG baseline', text: 'Calculates expected monthly PAYG cost based on your average daily ingestion and the rate you selected.' },
      { label: 'Lines 13–16: Tier projections', text: 'Projects each tier cost to a full month (30 days). Compares against PAYG to show savings.' },
      { label: 'Lines 17–22: Recommendation logic', text: 'case() selects the tier that offers the lowest monthly cost while accommodating your actual average ingestion. Falls back to PAYG if no tier saves money.' },
    ],
    tags: ['Cost Verification', 'FinOps'],
  },
  {
    id: 13,
    title: 'Table-Level Cost',
    description: 'Breaks down ingestion cost by Log Analytics table. Identifies expensive data sources and optimization opportunities.',
    template: `let CostPerGB = {{CostPerGB}};
let TimeRange = {{TimeRange}};
Usage
| where TimeGenerated between (startofday(ago(TimeRange)) .. startofday(now()))
| where IsBillable == true
| summarize TotalBytes = sum(Quantity), RowCount = count() by DataType
| extend TotalGB = round(TotalBytes / 1e9, 3)
| extend MonthlyCost = round(TotalGB * CostPerGB, 2)
| extend PercentOfTotal = round((TotalBytes / toscalar(Usage | where IsBillable == true | summarize TotalBytes = sum(Quantity) | project TotalBytes)) * 100, 1)
| project TableName = DataType, TotalGB, RowCount, MonthlyCost, PercentOfTotal
| sort by MonthlyCost desc`,
    explanations: [
      { label: 'Lines 1–5: Billable data per table', text: 'Aggregates bytes and row count from the Usage table grouped by DataType (Log Analytics table name).' },
      { label: 'Lines 6–8: Conversions', text: 'Converts bytes to GB and multiplies by your cost rate to estimate monthly cost for each table.' },
      { label: 'Line 9: Percentage calculation', text: 'toscalar() computes the total billable bytes across all tables, then divides each table\'s bytes to get its share (%).' },
      { label: 'Line 10: Projection', text: 'Selects and renames columns for clarity. MonthlyCost is estimated cost if you kept this ingestion rate for 30 days.' },
      { label: 'Line 11: Sort', text: 'Sorts by cost (descending) so expensive tables appear first. Useful for pinpointing optimization targets.' },
    ],
    tags: ['Cost Verification', 'FinOps'],
  },
  {
    id: 14,
    title: 'Restore vs Search',
    description: 'Estimates cost of restoring from Data Lake vs performing live KQL searches. Helps decide between hot search and cold restore.',
    template: `let TimeRange = {{TimeRange}};
let SearchCostPerGB = {{CostPerGB}};
let RestoreCostPerGB = 0.22;
let DataLakeStorageCostPerGB_Month = 0.018;
Usage
| where TimeGenerated between (startofday(ago(TimeRange)) .. startofday(now()))
| where IsBillable == true
| summarize TotalGB = sum(Quantity) / 1e9 by bin(TimeGenerated, 1d)
| extend AvgDailyGB = round(avg(TotalGB), 2)
| extend SearchCost_30Days = round(AvgDailyGB * 30 * SearchCostPerGB, 2)
| extend StoredGB_30Days = AvgDailyGB * 30
| extend DataLakeStorageCost_30Days = round(StoredGB_30Days * DataLakeStorageCostPerGB_Month, 2)
| extend RestoreCost_FullRestore = round(StoredGB_30Days * RestoreCostPerGB, 2)
| extend SearchAlone_Cost = SearchCost_30Days
| extend RecommendedPath = case(
    RestoreCost_FullRestore + DataLakeStorageCost_30Days < SearchCost_30Days, "Data Lake + Restore (save ingestion cost)",
    "Live Search (keep all hot)"
  )
| project AvgDailyGB, SearchCost_30Days, RestoreCost_FullRestore, DataLakeStorageCost_30Days, RecommendedPath
| sort by AvgDailyGB asc`,
    explanations: [
      { label: 'Lines 1–4: Rate definitions', text: 'SearchCostPerGB is your Analytics Logs rate. RestoreCostPerGB (£0.22/GB) is Azure data restore cost. Storage is £0.018/GB/month in Data Lake.' },
      { label: 'Lines 5–9: Daily aggregation', text: 'Calculates average daily ingestion from billable Usage, binned by day, then averaged across the time range.' },
      { label: 'Lines 10–13: Cost scenarios', text: 'Scenarios: (1) Keep everything hot in Analytics Logs (SearchCost), (2) Archive to Data Lake and restore when needed.' },
      { label: 'Lines 14–17: Break-even logic', text: 'case() compares total cost of Data Lake storage + restore for the month vs keeping all data hot in Analytics Logs.' },
      { label: 'Line 18: Recommendation', text: 'Suggests the cheaper path: either archival (lower initial ingestion cost) or live search (if restores are rare).' },
    ],
    note: 'Restore pricing assumes a one-time full restore per month. Adjust RestoreCostPerGB if your restore patterns differ.',
    tags: ['Cost Verification', 'FinOps'],
  },
];
